import React, { PropsWithChildren } from 'react'
import { Loading, Message } from '@workwhile/ui'
import { createContext } from 'lib/createContext'
import { parseErrorMessage } from 'api'
import { useConvertColumbiaTimesheetToVndlyUploadMutation } from 'queries/admin/vndly/useConvertColumbiaTimesheetToVndlyUploadMutation'
import { useDiffColumbiaTimesheetMutation } from 'queries/admin/vndly/useDiffColumbiaTimesheetMutation'
import { useVndlyInstancesQuery } from 'queries/admin/vndly/useVndlyInstancesQuery'

type ColumbiaTimesheetContextState = {
  state: {
    instances: NonNullable<
      ReturnType<typeof useVndlyInstancesQuery>['data']
    >['items']
    diffMutationState: {
      isPending: boolean
      isSuccess: boolean
      isError: boolean
      errorMessage: string | undefined
      data: ReturnType<typeof useDiffColumbiaTimesheetMutation>['data']
    }
    convertMutationState: {
      isPending: boolean
      isSuccess: boolean
      isError: boolean
      errorMessage: string | undefined
      data: ReturnType<
        typeof useConvertColumbiaTimesheetToVndlyUploadMutation
      >['data']
    }
  }
  actions: {
    compareTimesheet: ReturnType<
      typeof useDiffColumbiaTimesheetMutation
    >['mutate']
    resetCompare: ReturnType<typeof useDiffColumbiaTimesheetMutation>['reset']
    generateTimesheetCsv: ReturnType<
      typeof useConvertColumbiaTimesheetToVndlyUploadMutation
    >['mutate']
    resetGenerate: ReturnType<
      typeof useConvertColumbiaTimesheetToVndlyUploadMutation
    >['reset']
  }
}

const [Context, useColumbiaTimesheetContextValue] =
  createContext<ColumbiaTimesheetContextState>({
    name: 'ColumbiaTimesheetContext',
  })

export const ColumbiaTimesheetProvider = ({ children }: PropsWithChildren) => {
  const {
    data: instancesData,
    isLoading: isInstancesLoading,
    isError: isInstancesError,
  } = useVndlyInstancesQuery()
  const {
    mutate: compareTimesheet,
    reset: resetCompare,
    isPending: isDiffPending,
    isSuccess: isDiffSuccess,
    isError: isDiffError,
    data: diffData,
    error: diffError,
  } = useDiffColumbiaTimesheetMutation()
  const {
    mutate: generateTimesheetCsv,
    reset: resetGenerate,
    isPending: isConvertPending,
    isSuccess: isConvertSuccess,
    isError: isConvertError,
    data: convertData,
    error: convertError,
  } = useConvertColumbiaTimesheetToVndlyUploadMutation()

  if (isInstancesLoading) {
    return <Loading />
  }

  if (isInstancesError || !instancesData) {
    return (
      <Message variant="error">
        Sorry, something went wrong loading VNDLY instances. Please reach out to
        eng support if this persists.
      </Message>
    )
  }

  const value: ColumbiaTimesheetContextState = {
    state: {
      instances: instancesData.items,
      diffMutationState: {
        isPending: isDiffPending,
        isSuccess: isDiffSuccess,
        isError: isDiffError,
        errorMessage: parseErrorMessage(diffError),
        data: diffData,
      },
      convertMutationState: {
        isPending: isConvertPending,
        isSuccess: isConvertSuccess,
        isError: isConvertError,
        errorMessage: convertError?.message,
        data: convertData,
      },
    },
    actions: {
      compareTimesheet,
      resetCompare,
      generateTimesheetCsv,
      resetGenerate,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useColumbiaTimesheetContextValue }
